import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SourceSearch from '../components/SourceSearch';
import { Button, Dialog } from '../ui';
import { codeLabel } from './codes';

/**
 * A scanned code nobody knew, searched by text (phase 4 §5.6): the code is
 * kept, and the result picked here is added with it — which teaches the
 * installation that barcode for films and games. Also the place to
 * pick among several editions a code leads to (`candidates`), and to name
 * the right entry of an item linked to the wrong one (relink: `initialQuery`
 * starts the search from its title).
 */
function TextSearchDialog({ itemType, code, lang, candidates, onPick, onClose, title, description, initialQuery, pickLabel, initialPlatform }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(null);
  // A game's search starts on the copy's platform (the row's, the pile's, the item's: `initialPlatform`).
  const [platform, setPlatform] = useState(initialPlatform || '');
  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={title || t('scan.search.title')}
      description={description || (code ? t('scan.search.keeps', { code: codeLabel(code) }) : undefined)}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={() => selected && onPick(selected)} disabled={!selected}>{pickLabel || t('scan.search.pick')}</Button>
        </>
      )}
    >
      {candidates && candidates.length > 0 ? (
        <ul className="tmdb-results scan-choices" aria-label={t('scan.search.editions')}>
          {candidates.map((candidate) => {
            const key = `${candidate.source}:${candidate.source_id}`;
            const isSelected = selected && `${selected.source}:${selected.source_id}` === key;
            return (
              <li key={key}>
                <button type="button" className={`source-result${isSelected ? ' is-selected' : ''}`} aria-pressed={!!isSelected}
                        onClick={() => setSelected(candidate)}>
                  {candidate.thumb
                    ? <img className="source-poster" src={candidate.thumb} alt="" loading="lazy" />
                    : <span className="source-poster source-poster--empty" aria-hidden="true" />}
                  <span className="source-result__text">
                    <span className="source-result__title">{candidate.title}</span>
                    {candidate.creators && candidate.creators.length > 0 && <span className="source-result__overview">{candidate.creators.join(', ')}</span>}
                    <span className="source-result__meta">{[candidate.year, candidate.publisher].filter(Boolean).join(' · ')}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <SourceSearch itemType={itemType} lang={lang} selected={selected} onSelect={setSelected} initialQuery={initialQuery}
                      platform={platform} onPlatform={setPlatform} />
      )}
    </Dialog>
  );
}

export default TextSearchDialog;
